<script lang="ts">
  import {
    type CalendarEvent,
    type CalendarDay,
    type LaneAssignable,
    CalendarCellInteraction,
    getSingleDayEvents,
    getSpanTypeClass,
    assignLanes,
    maxLaneCount,
    MAX_VISIBLE_EVENTS,
    MONTH_NAMES,
    WEEKDAY_LABELS,
    eventColor,
    eventEndDate,
    eventTotalDays,
    isMultiDayEvent,
    getTodayStr,
    formatEventTimeShort,
    hoveredDateStr,
    getWeekDates,
    cellClickMode,
    openEventModalForEdit,
  } from '../lib/calendarViewHelpers.svelte';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    year: number;
    events: CalendarEvent[];
    eventsByDate: Map<string, CalendarEvent[]>;
  }
  let { year, events, eventsByDate }: Props = $props();
  const ci = new CalendarCellInteraction(() => events);

  const SPAN_ROW_HEIGHT = 10;
  const DAY_NUMBER_HEIGHT = 10;

  interface DayCell extends CalendarDay {
    colIndex: number;
  }
  interface MonthRow {
    month: number;
    name: string;
    firstWeekOffset: number;
    weekSpan: number;
    days: DayCell[];
  }
  interface MonthSpan extends LaneAssignable {
    event: CalendarEvent;
    continuesLeft: boolean;
    continuesRight: boolean;
  }

  // ── Data computation ───────────────────────────────────────────────────────

  function computeMonthRows(
    yr: number,
    ebd: Map<string, CalendarEvent[]>,
  ): { rows: MonthRow[]; totalWeekColumns: number } {
    const todayStr = getTodayStr();
    const rows: MonthRow[] = [];
    for (let m = 0; m < 12; m++) {
      const daysInMonth = new Date(yr, m + 1, 0).getDate();
      const firstDayWeekday = new Date(yr, m, 1).getDay();
      const weekSpan = Math.ceil((firstDayWeekday + daysInMonth) / 7);
      const days: DayCell[] = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(yr, m, d);
        const dateStr = `${yr}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const weekday = date.getDay();
        const weekInMonth = Math.floor((firstDayWeekday + d - 1) / 7);
        days.push({
          day: d,
          dateStr,
          weekday,
          isToday: dateStr === todayStr,
          isWeekend: weekday === 0 || weekday === 6,
          events: ebd.get(dateStr) || [],
          colIndex: weekInMonth * 7 + weekday,
        });
      }
      rows.push({ month: m, name: MONTH_NAMES[m], firstWeekOffset: 0, weekSpan, days });
    }
    const totalWeekColumns = 37;
    return { rows, totalWeekColumns };
  }

  let monthRows = $derived(computeMonthRows(year, eventsByDate));

  function getMonthSpans(monthData: MonthRow): MonthSpan[] {
    const m = monthData.month;
    const daysInMonth = new Date(year, m + 1, 0).getDate();
    const monthStart = `${year}-${String(m + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(m + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

    const dayToCol = new Map<number, number>();
    for (const d of monthData.days) {
      dayToCol.set(d.day, d.colIndex);
    }

    const spans: MonthSpan[] = events
      .filter((e) => isMultiDayEvent(e) && e.date <= monthEnd && eventEndDate(e) >= monthStart)
      .map((e) => {
        const eStartDate = e.date < monthStart ? monthStart : e.date;
        const eEndDate = eventEndDate(e) > monthEnd ? monthEnd : eventEndDate(e);
        const startDay = parseInt(eStartDate.split('-')[2], 10);
        const endDay = parseInt(eEndDate.split('-')[2], 10);

        return {
          event: e,
          startCol: dayToCol.get(startDay) ?? 0,
          endCol: dayToCol.get(endDay) ?? 0,
          lane: 0,
          continuesLeft: e.date < monthStart,
          continuesRight: eventEndDate(e) > monthEnd,
        };
      });

    return assignLanes(spans);
  }

  // ── Week highlight ─────────────────────────────────────────────────────────

  let weekDates = $derived($hoveredDateStr ? getWeekDates($hoveredDateStr) : new Set<string>());
</script>

<div class="weekday-container">
  <div
    class="grid-row header-row"
    style="grid-template-columns: 1.5rem repeat({monthRows.totalWeekColumns}, 1fr);"
  >
    <div class="month-label-cell"></div>
    {#each Array.from({ length: monthRows.totalWeekColumns }, (_, i) => i) as colIdx}
      {@const weekdayIdx = colIdx % 7}
      {@const isWeekBoundary = colIdx > 0 && weekdayIdx === 0}
      <div
        class="col-header"
        class:weekend-header={weekdayIdx === 0 || weekdayIdx === 6}
        class:week-boundary={isWeekBoundary}
      >
        {WEEKDAY_LABELS[weekdayIdx]}
      </div>
    {/each}
  </div>

  {#each monthRows.rows as monthData}
    {@const spans = getMonthSpans(monthData)}
    {@const laneCount = maxLaneCount(spans)}
    {@const spanPadding = laneCount * SPAN_ROW_HEIGHT}
    <div
      class="grid-row"
      style="grid-template-columns: 1.5rem repeat({monthRows.totalWeekColumns}, 1fr);"
    >
      <div class="month-label-cell">
        <span class="month-name">{monthData.name}</span>
      </div>
      {#each Array.from({ length: monthRows.totalWeekColumns }, (_, i) => i) as colIdx}
        {@const dayForCol = monthData.days.find((d: DayCell) => d.colIndex === colIdx)}
        {@const weekdayIdx = colIdx % 7}
        {@const isWeekBoundary = colIdx > 0 && weekdayIdx === 0}
        {#if dayForCol}
          {@const singleDayEvents = getSingleDayEvents(dayForCol.events)}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="day-cell"
            class:today={dayForCol.isToday}
            class:weekend={dayForCol.isWeekend}
            class:has-events={dayForCol.events.length > 0}
            class:week-boundary={isWeekBoundary}
            class:drop-target={ci.dropTargetDate === dayForCol.dateStr}
            class:week-highlight={weekDates.has(dayForCol.dateStr)}
            class:hovered-cell={$hoveredDateStr === dayForCol.dateStr}
            onclick={() => {
              if ($cellClickMode === 'single') ci.cellClick(dayForCol.dateStr);
            }}
            ondblclick={() => {
              if ($cellClickMode === 'double') ci.cellClick(dayForCol.dateStr);
            }}
            onmouseenter={(e) =>
              ci.cellEnter(
                e,
                dayForCol.dateStr,
                `${WEEKDAY_LABELS[dayForCol.weekday]} ${monthData.name} ${dayForCol.day}`,
                dayForCol.events,
              )}
            onmouseleave={ci.cellLeave}
            ondragover={(e) => ci.cellDragOver(e, dayForCol.dateStr)}
            ondragleave={ci.cellDragLeave}
            ondrop={(e) => ci.cellDrop(e, dayForCol.dateStr)}
          >
            <span class="day-number">{dayForCol.day}</span>
            {#if spanPadding > 0}
              <div class="span-spacer" style="height: {spanPadding}px;"></div>
            {/if}
            {#if singleDayEvents.length > 0}
              <div class="event-pills">
                {#each singleDayEvents.slice(0, MAX_VISIBLE_EVENTS) as event (event.id)}
                  {@const timeStr = formatEventTimeShort(event)}
                  <button
                    class="event-pill"
                    style="border-left-color: {eventColor(event.type)}"
                    class:dragging={ci.draggingEventId === event.id}
                    draggable={!$viewport.isMobile}
                    ondragstart={(e) => ci.pillDragStart(e, event)}
                    ondragend={ci.pillDragEnd}
                    onclick={(e) => {
                      e.stopPropagation();
                      openEventModalForEdit(event);
                    }}
                  >
                    {#if timeStr}<span class="event-time">{timeStr}</span>{/if}{event.title}
                  </button>
                {/each}
                {#if singleDayEvents.length > MAX_VISIBLE_EVENTS}
                  <span class="event-more">+{singleDayEvents.length - MAX_VISIBLE_EVENTS} more</span
                  >
                {/if}
              </div>
            {/if}
          </div>
        {:else}
          <div class="day-cell empty" class:week-boundary={isWeekBoundary}></div>
        {/if}
      {/each}

      <!-- Spanning bars overlaid on the month row -->
      {#if spans.length > 0}
        <div
          class="spanning-layer"
          style="grid-column: 2 / -1; grid-row: 1; top: {DAY_NUMBER_HEIGHT}px; height: {spanPadding}px;"
        >
          {#each spans as span (span.event.id)}
            <button
              type="button"
              class="spanning-bar {getSpanTypeClass(span.event.type)}"
              class:continues-left={span.continuesLeft}
              class:continues-right={span.continuesRight}
              class:dragging={ci.draggingEventId === span.event.id}
              draggable={!$viewport.isMobile}
              ondragstart={(e) => ci.pillDragStart(e, span.event)}
              ondragend={ci.pillDragEnd}
              onclick={(e) => {
                e.stopPropagation();
                ci.spanClick(span.event);
              }}
              style="
                top: {span.lane * SPAN_ROW_HEIGHT}px;
                left: calc({span.startCol} / {monthRows.totalWeekColumns} * 100%);
                width: calc({span.endCol -
                span.startCol +
                1} / {monthRows.totalWeekColumns} * 100%);
              "
              title="{span.event.title} ({eventTotalDays(span.event)} days)"
            >
              <span class="spanning-title">{span.event.title}</span>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  {/each}
</div>

<style>
  @import '../lib/calendarCellStyles.css';

  .weekday-container {
    width: calc(100% - 1rem);
    height: calc(100% - 1rem);
    margin: 0.5rem;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 1px;
    background: #e5e7eb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-sizing: border-box;
  }

  .grid-row {
    display: grid;
    gap: 1px;
    min-height: 0;
    flex: 1;
    position: relative;
  }
  .header-row {
    flex: 0 0 auto;
    min-height: 1.25rem;
  }

  .month-label-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    padding: 0.125rem;
    overflow: hidden;
  }

  .month-name {
    font-size: 0.5625rem;
    font-weight: 800;
    color: #374151;
    letter-spacing: 0.05em;
    writing-mode: vertical-lr;
    transform: rotate(180deg);
    white-space: nowrap;
  }

  .col-header {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    font-size: 0.5rem;
    font-weight: 600;
    color: #6b7280;
  }
  .col-header.weekend-header {
    background: #e5e7eb;
    color: #9ca3af;
  }
  .col-header.week-boundary {
    border-left: 2px solid #d1d5db;
  }

  .day-cell.week-boundary {
    border-left: 2px solid #d1d5db;
  }

  .day-number {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #374151;
    line-height: 1;
  }
</style>
